import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useMatch, useParams } from 'react-router-dom';
import { makeStyles, mergeClasses, tokens, Spinner } from '@coreai/fluentui-react';
import { Folder20Filled } from '@fluentui/react-icons';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { ApiError } from '@/services/apiError';
import { projectNavSections } from '@/components/Sidebar/projectSidebarConfig';
import { Sidebar, type SidebarSection } from '@/components/Sidebar/Sidebar';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { paths } from '@/routes';
import { tenantPrefix } from '@/routes';
import { useAppShellStyles } from './AppShell.styles';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

// Sections are mostly static. Two pieces need runtime context:
//   1. The Home item's path must be tenant-aware so clicking Home from
//      inside a tenant doesn't drop the user back to the bare root.
//   2. The project section heading shows the active project's name plus a
//      folder icon — both resolved per-render via useMemo below.
const STATIC_SIDEBAR_SECTIONS: SidebarSection[] = projectNavSections.map((s) => ({
  id: s.id,
  label: s.label,
  ...(s.icon ? { icon: s.icon } : {}),
  items: s.items.map((it) => ({
    id: it.id,
    label: it.label,
    icon: it.icon,
    activeIcon: it.activeIcon,
    path: it.path,
    end: it.end,
  })),
}));

const useStyles = makeStyles({
  mainContent: {
    overflow: 'auto',
    // Asymmetric top padding pulls page-level headings up so they sit
    // alongside the sidebar Home button instead of leaving a large gap
    // under the global header. Mirrors `AppLayout.content` so project- and
    // app-scoped pages share the same outer chrome. Nested app routes
    // (`apps/:appName/*`) take over via AppLayout and apply their own
    // padding, so the values won't compound.
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      paddingTop: tokens.spacingVerticalS,
      paddingRight: tokens.spacingHorizontalM,
      paddingBottom: tokens.spacingVerticalL,
      paddingLeft: tokens.spacingHorizontalM,
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXXL,
  },
});

export const ProjectLayout = () => {
  const styles = useStyles();
  const shellStyles = useAppShellStyles();
  const { trackEvent } = useTelemetry();
  const { tenantId: routeTenantId } = useParams<{ tenantId?: string }>();
  const { projectName, project, locationName, isLoading, notFound, error } = useCurrentProject();
  const locationInStore = useAppStore((state) => state.selectedLocation);
  const selectedTenantId = useAppStore((state) => state.selectedTenantId);
  const prevProjectRef = useRef('');

  // Inject runtime context into the static section template:
  //  - Home item path becomes tenant-aware so it doesn't escape the tenant.
  //  - Project section heading shows the current environment's display name and
  //    a folder icon, with `indentItems` so the features visually nest under
  //    it. We fall back to the route segment (and finally "Environment") while
  //    the environment is still loading so the heading never disappears.
  const sections = useMemo<SidebarSection[]>(() => {
    const homeHref = tenantPrefix(selectedTenantId) || '/';
    const projectDisplayName = project?.name || projectName || 'Environment';
    return STATIC_SIDEBAR_SECTIONS.map((section) => {
      if (section.id === 'home') {
        return {
          ...section,
          items: section.items.map((it) => (it.id === 'home' ? { ...it, path: homeHref } : it)),
        };
      }
      if (section.id === 'project') {
        return {
          ...section,
          categoryLabel: 'Environment',
          label: projectDisplayName,
          icon: Folder20Filled,
          brandIcon: true,
          indentItems: true,
        };
      }
      return section;
    });
  }, [selectedTenantId, project, projectName]);

  const handleSidebarItemClick = useCallback(
    (id: string) => trackEvent('navigation.sidebar.clicked', { destination: id }),
    [trackEvent],
  );

  // When inside an app, AppLayout owns the sidebar — ProjectLayout just passes through.
  const matchBare = useMatch('/location/:locationName/projects/:projectName/apps/:appName/*');
  const matchTenant = useMatch('/tenant/:tenantId/location/:locationName/projects/:projectName/apps/:appName/*');
  const isInsideApp = !!(matchBare || matchTenant);

  // Bridge: sync URL-derived project context to the store ONLY after the project
  // is confirmed to exist. Avoids persisting invalid project names from bad URLs,
  // which would cause redirect loops via LastSessionRedirect.
  // Also seeds `selectedLocation` from the URL — without this, deep-link entry
  // into a project route leaves the operation-picker prefetch scope unresolved
  // (see useScopeKey in useAllOperations).
  useEffect(() => {
    if (!projectName || isLoading || notFound || !project) return;
    const projectKey = `${routeTenantId ?? ''}|${locationName ?? ''}|${projectName}`;
    if (projectKey !== prevProjectRef.current) {
      prevProjectRef.current = projectKey;
      useAppStore.getState().hydrateRouteContext({ projectName, ...(locationName ? { locationName } : {}) });
      const entryTenantId = routeTenantId ?? useAppStore.getState().selectedTenantId;
      // Prefer the tenant URL param when present and fall back to the store
      // for non-tenant routes or before store hydration catches up. Using
      // only the store caused recordAccess to no-op on earlier navigation.
      const recordLocation = locationName ?? locationInStore;
      if (entryTenantId && recordLocation) {
        useRecentWorkStore.getState().recordAccess({
          type: 'project',
          displayName: projectName,
          location: recordLocation,
          project: projectName,
          app: '',
          href: paths.projectApps(recordLocation, projectName, routeTenantId),
          tenantId: entryTenantId,
        });
      }
    }
  }, [projectName, locationName, locationInStore, project, isLoading, notFound, routeTenantId]);

  if (isLoading) {
    return (
      <div className={shellStyles.body}>
        <div className={mergeClasses(shellStyles.main, styles.loadingContainer)}>
          <Spinner size="medium" label="Loading environment…" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={shellStyles.body}>
        <div className={shellStyles.main}>
          <ResourceErrorPage resourceType="project" error={new ApiError('Not found', 404)} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shellStyles.body}>
        <div className={shellStyles.main}>
          <ResourceErrorPage resourceType="project" error={error} />
        </div>
      </div>
    );
  }

  // Inside an app — no project sidebar, AppLayout handles its own sidebar
  if (isInsideApp) {
    return <Outlet />;
  }

  return (
    <div className={shellStyles.body}>
      <Sidebar
        ariaLabel="Environment navigation"
        navAriaLabel="Environment"
        sections={sections}
        onItemClick={handleSidebarItemClick}
      />
      <div className={mergeClasses(shellStyles.main, styles.mainContent)}>
        <Outlet />
      </div>
    </div>
  );
};
